/**
 * Garage Matrix View
 * 2D grid showing tools by different dimensions (Role × Sales Stage, etc.)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';

// Fallback dimension constants (used if props are empty)
const FALLBACK_JOURNEYS = ['Prospect', 'Qualify', 'Demo/Eval', 'Proposal', 'Onboard', 'Expand', 'Adopt', 'Retain', 'Backstage'];
const FALLBACK_ROLES = ['AE', 'SE', 'CSM', 'TAM', 'BDR', 'Channel Manager', 'Leadership'];
const FALLBACK_CAPABILITIES = ['Research', 'Generate', 'Analyze', 'Calculate', 'Automate', 'Visualize'];
const FALLBACK_PERSONAS = ['Sales', 'Customer Success', 'Product', 'Engineering', 'Marketing', 'Support'];

// Subheads for role × stage cells
const CELL_SUBHEADS = {
  'AE|Prospect': 'Account Research',
  'AE|Qualify': 'Discovery and Account Plan',
  'AE|Demo/Eval': 'Demo Prep and Follow-Up',
  'AE|Proposal': 'Proposal and Negotiation',
  'SE|Demo/Eval': 'Demo Customization',
  'SE|Proposal': 'Technical Validation',
  'CSM|Onboard': 'Customer Onboarding',
  'CSM|Expand': 'Account Health & EBR Prep',
  'TAM|Onboard': 'Technical Onboarding',
  'BDR|Prospect': 'Lead Enrichment',
  'BDR|Qualify': 'Sequencing and Cadence',
};

const TOOLTIP_WIDTH = 280;
const TOOLTIP_GAP = 8;
const CELL_POPOVER_WIDTH = 320;
const MAX_VISIBLE_CHIPS = 6;

const GarageMatrixView = ({
  tools,
  onCellClick,
  onShowDetails,
  onLaunchTool,
  // Dynamic dimension options from config API
  journeys = FALLBACK_JOURNEYS,
  roles = FALLBACK_ROLES,
  capabilities = FALLBACK_CAPABILITIES,
  personas = FALLBACK_PERSONAS
}) => {
  // Build matrix type configurations with dynamic options
  const MATRIX_TYPES = [
    {
      id: 'role-stage',
      label: 'Role × Sales stage',
      rowDimension: 'role',
      colDimension: 'journey',
      rows: roles.length > 0 ? roles : FALLBACK_ROLES,
      cols: journeys.length > 0 ? journeys : FALLBACK_JOURNEYS,
      rowLabel: 'Role',
      colLabel: 'Stage'
    },
    {
      id: 'role-capability',
      label: 'Role × Capability',
      rowDimension: 'role',
      colDimension: 'capability',
      rows: roles.length > 0 ? roles : FALLBACK_ROLES,
      cols: capabilities.length > 0 ? capabilities : FALLBACK_CAPABILITIES,
      rowLabel: 'Role',
      colLabel: 'Capability'
    },
    {
      id: 'persona-stage',
      label: 'Persona × Sales stage',
      rowDimension: 'persona',
      colDimension: 'journey',
      rows: personas.length > 0 ? personas : FALLBACK_PERSONAS,
      cols: journeys.length > 0 ? journeys : FALLBACK_JOURNEYS,
      rowLabel: 'Persona',
      colLabel: 'Stage'
    }
  ];

  const [matrixType, setMatrixType] = useState(MATRIX_TYPES[0]); // Default: Role × Sales stage
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const scrollRef = useRef(null);
  const cellPopoverRef = useRef(null);
  const [cellStats, setCellStats] = useState({ empty: 0, low: 0, medium: 0, high: 0 });
  const [tooltip, setTooltip] = useState(null); // { tool, style, placement }
  // Popover for "+N more" — does not resize table cells/rows
  const [cellPopover, setCellPopover] = useState(null); // { row, col, tools, style }

  const clearTooltip = useCallback(() => {
    setTooltip(null);
  }, []);

  const closeCellPopover = useCallback(() => {
    setCellPopover(null);
  }, []);

  const openCellPopover = useCallback((event, row, col, cellTools) => {
    clearTooltip();
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = rect.left;
    left = Math.max(12, Math.min(left, viewportWidth - CELL_POPOVER_WIDTH - 12));

    const estimatedHeight = Math.min(360, 72 + cellTools.length * 44);
    const spaceBelow = viewportHeight - rect.bottom;
    const placeBelow = spaceBelow >= estimatedHeight || spaceBelow >= rect.top;

    const style = placeBelow
      ? {
          top: rect.bottom + TOOLTIP_GAP,
          left,
          width: CELL_POPOVER_WIDTH,
          maxHeight: Math.min(360, Math.max(160, spaceBelow - 24)),
        }
      : {
          bottom: viewportHeight - rect.top + TOOLTIP_GAP,
          left,
          width: CELL_POPOVER_WIDTH,
          maxHeight: Math.min(360, Math.max(160, rect.top - 24)),
        };

    setCellPopover({
      row,
      col,
      tools: cellTools,
      style,
      placement: placeBelow ? 'below' : 'above',
    });
  }, [clearTooltip]);

  const showTooltipForChip = useCallback((event, tool, preferBelow) => {
    // Don't show chip tooltip while the cell popover is open over the same interaction
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    left = Math.max(12, Math.min(left, viewportWidth - TOOLTIP_WIDTH - 12));

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placeBelow = preferBelow
      ? spaceBelow >= 120 || spaceBelow >= spaceAbove
      : spaceAbove < 120 && spaceBelow > spaceAbove;

    const style = placeBelow
      ? { top: rect.bottom + TOOLTIP_GAP, left, width: TOOLTIP_WIDTH }
      : { bottom: viewportHeight - rect.top + TOOLTIP_GAP, left, width: TOOLTIP_WIDTH };

    setTooltip({
      tool,
      style,
      placement: placeBelow ? 'below' : 'above',
    });
  }, []);

  // Update matrix type when dimensions change
  useEffect(() => {
    if (MATRIX_TYPES.length > 0) {
      setMatrixType(MATRIX_TYPES[0]);
      closeCellPopover();
      clearTooltip();
    }
  }, [journeys.join(','), roles.join(','), capabilities.join(','), personas.join(',')]);

  const abbreviateToolName = (name) => {
    if (name.length <= 30) return name;

    let abbreviated = name
      .replace(/RingCentral/g, 'RC')
      .replace(/FY\d+\s+/g, '')
      .replace(/\s+\(.*?\)/g, '')
      .replace(/\s+-\s+/g, ' ');

    if (abbreviated.length > 30) {
      abbreviated = abbreviated.substring(0, 27) + '...';
    }

    return abbreviated;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  // Close cell popover on outside click / Escape
  useEffect(() => {
    if (!cellPopover) return undefined;

    const handlePointerDown = (event) => {
      if (cellPopoverRef.current && !cellPopoverRef.current.contains(event.target)) {
        // Allow the "+N more" button itself to toggle via its own handler
        if (event.target.closest?.('.garage-matrix-cell-more')) return;
        closeCellPopover();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeCellPopover();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [cellPopover, closeCellPopover]);

  // Clear overlays on page/matrix scroll or resize — but not when scrolling inside the popover
  useEffect(() => {
    const scrollEl = scrollRef.current;

    const isInsideCellPopover = (target) =>
      Boolean(
        cellPopoverRef.current?.contains(target)
        || (target instanceof Element && target.closest?.('.garage-matrix-cell-popover'))
      );

    const handleScroll = (event) => {
      if (isInsideCellPopover(event.target)) return;
      clearTooltip();
      closeCellPopover();
    };

    const handleResize = () => {
      clearTooltip();
      closeCellPopover();
    };

    scrollEl?.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      scrollEl?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [clearTooltip, closeCellPopover]);

  const getSubhead = (row, col) => {
    const key = `${row}|${col}`;
    return CELL_SUBHEADS[key] || null;
  };

  const getToolCount = (rowValue, colValue) => {
    return tools.filter(tool => {
      let matchRow = false;
      let matchCol = false;

      if (matrixType.rowDimension === 'role') {
        matchRow = tool.roles?.includes(rowValue);
      } else if (matrixType.rowDimension === 'persona') {
        matchRow = tool.personas?.includes(rowValue);
      }

      if (matrixType.colDimension === 'journey') {
        matchCol = tool.journeys?.includes(colValue);
      } else if (matrixType.colDimension === 'capability') {
        matchCol = tool.capabilities?.includes(colValue);
      }

      return matchRow && matchCol;
    }).length;
  };

  const getToolsForCell = (rowValue, colValue) => {
    return tools.filter(tool => {
      let matchRow = false;
      let matchCol = false;

      if (matrixType.rowDimension === 'role') {
        matchRow = tool.roles?.includes(rowValue);
      } else if (matrixType.rowDimension === 'persona') {
        matchRow = tool.personas?.includes(rowValue);
      }

      if (matrixType.colDimension === 'journey') {
        matchCol = tool.journeys?.includes(colValue);
      } else if (matrixType.colDimension === 'capability') {
        matchCol = tool.capabilities?.includes(colValue);
      }

      return matchRow && matchCol;
    });
  };

  useEffect(() => {
    const stats = { empty: 0, low: 0, medium: 0, high: 0 };

    matrixType.rows.forEach(row => {
      matrixType.cols.forEach(col => {
        const count = getToolCount(row, col);
        if (count === 0) stats.empty++;
        else if (count <= 2) stats.low++;
        else if (count <= 5) stats.medium++;
        else stats.high++;
      });
    });

    setCellStats(stats);
  }, [matrixType, tools]);

  const tooltipNode = tooltip
    ? createPortal(
        <div
          className={`garage-matrix-tooltip garage-matrix-tooltip-portal tooltip-${tooltip.placement}`}
          style={tooltip.style}
          role="tooltip"
        >
          <div className="garage-matrix-tooltip-header">
            <div className="garage-matrix-tooltip-icon">
              {tooltip.tool.imageUrl ? (
                <img src={tooltip.tool.imageUrl} alt={tooltip.tool.title} />
              ) : (
                <div className="garage-matrix-tooltip-icon-placeholder">
                  {tooltip.tool.title.charAt(0)}
                </div>
              )}
            </div>
            <div className="garage-matrix-tooltip-content">
              <div className="garage-matrix-tooltip-title-row">
                <div className="garage-matrix-tooltip-title">{tooltip.tool.title}</div>
                <div className={`garage-matrix-tooltip-badge ${tooltip.tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                  {tooltip.tool.toolType === 'Gem' ? 'GEM' : 'APP'}
                </div>
              </div>

              {tooltip.tool.description && (
                <div className="garage-matrix-tooltip-description">
                  {tooltip.tool.description.length > 100
                    ? tooltip.tool.description.substring(0, 100) + '...'
                    : tooltip.tool.description}
                </div>
              )}

              <div className="garage-matrix-tooltip-link">
                Click to open →
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  const cellPopoverNode = cellPopover
    ? createPortal(
        <div
          ref={cellPopoverRef}
          className={`garage-matrix-cell-popover tooltip-${cellPopover.placement}`}
          style={cellPopover.style}
          role="dialog"
          aria-label={`${cellPopover.row} × ${cellPopover.col} tools`}
        >
          <div className="garage-matrix-cell-popover-header">
            <div>
              <div className="garage-matrix-cell-popover-title">
                {cellPopover.row} × {cellPopover.col}
              </div>
              <div className="garage-matrix-cell-popover-subtitle">
                {cellPopover.tools.length} tool{cellPopover.tools.length === 1 ? '' : 's'}
              </div>
            </div>
            <button
              type="button"
              className="garage-matrix-cell-popover-close"
              onClick={closeCellPopover}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="garage-matrix-cell-popover-list">
            {cellPopover.tools.map((tool) => (
              <button
                type="button"
                key={tool.id}
                className={`garage-matrix-cell-popover-item ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}
                onClick={() => {
                  closeCellPopover();
                  if (onLaunchTool) onLaunchTool(tool);
                }}
              >
                <div className="garage-matrix-cell-popover-item-icon">
                  {tool.imageUrl ? (
                    <img src={tool.imageUrl} alt="" />
                  ) : (
                    <span>{tool.title?.charAt(0) || '?'}</span>
                  )}
                </div>
                <div className="garage-matrix-cell-popover-item-body">
                  <div className="garage-matrix-cell-popover-item-title">{tool.title}</div>
                  {tool.description && (
                    <div className="garage-matrix-cell-popover-item-desc">
                      {tool.description.length > 90
                        ? `${tool.description.substring(0, 90)}...`
                        : tool.description}
                    </div>
                  )}
                </div>
                <span className={`garage-matrix-tooltip-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                  {tool.toolType === 'Gem' ? 'GEM' : 'APP'}
                </span>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="garage-matrix-container">
      <div className="garage-matrix-toolbar">
        <div className="garage-matrix-type-selector">
          <label className="garage-matrix-type-label">View:</label>
          <div className="garage-matrix-dropdown-wrap" ref={dropdownRef}>
            <button
              className="garage-matrix-type-btn"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {matrixType.label}
              <ChevronDown size={14} />
            </button>

            {dropdownOpen && (
              <div className="garage-matrix-dropdown-menu">
                {MATRIX_TYPES.map(type => (
                  <div
                    key={type.id}
                    className={`garage-matrix-dropdown-item ${type.id === matrixType.id ? 'active' : ''}`}
                    onClick={() => {
                      setMatrixType(type);
                      closeCellPopover();
                      clearTooltip();
                      setDropdownOpen(false);
                    }}
                  >
                    {type.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <span className="garage-matrix-helper">Hover a chip for details — +N more opens full list</span>
      </div>

      <div
        className="garage-matrix-scroll"
        ref={scrollRef}
        onScroll={() => {
          clearTooltip();
          closeCellPopover();
        }}
      >
        <table className="garage-matrix-table">
          <thead>
            <tr>
              <th className="garage-matrix-corner">{matrixType.rowLabel} / {matrixType.colLabel}</th>
              {matrixType.cols.map(col => (
                <th key={col} className="garage-matrix-header">
                  <div className="garage-matrix-header-text">{col}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrixType.rows.map(row => (
              <tr key={row}>
                <th className="garage-matrix-row-header">{row}</th>
                {matrixType.cols.map(col => {
                  const count = getToolCount(row, col);
                  const cellTools = getToolsForCell(row, col);
                  const cellClass = count === 0 ? 'empty' :
                                   count <= 2 ? 'low' :
                                   count <= 5 ? 'medium' : 'high';
                  // Always keep grid compact — never expand cell/row height
                  const visibleTools = cellTools.slice(0, MAX_VISIBLE_CHIPS);
                  const isPopoverOpen =
                    cellPopover?.row === row && cellPopover?.col === col;

                  const subhead = (matrixType.rowDimension === 'role' && matrixType.colDimension === 'journey')
                    ? getSubhead(row, col)
                    : null;

                  return (
                    <td
                      key={col}
                      className={`garage-matrix-cell garage-matrix-cell-${cellClass}`}
                    >
                      {count === 0 ? (
                        null
                      ) : (
                        <div className="garage-matrix-cell-content">
                          {subhead && <div className="garage-matrix-cell-subhead">{subhead}</div>}
                          <div className="garage-matrix-cell-chips">
                            {visibleTools.map((tool, chipIndex) => {
                              const chipKey = `${row}-${col}-${tool.id}-${chipIndex}`;
                              const rowIndex = matrixType.rows.indexOf(row);
                              const preferBelow = rowIndex <= 1;

                              return (
                              <div
                                key={chipKey}
                                className={`garage-matrix-tool-chip ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}
                                onMouseEnter={(e) => {
                                  if (!cellPopover) showTooltipForChip(e, tool, preferBelow);
                                }}
                                onMouseLeave={clearTooltip}
                                onFocus={(e) => {
                                  if (!cellPopover) showTooltipForChip(e, tool, preferBelow);
                                }}
                                onBlur={clearTooltip}
                                onClick={() => {
                                  clearTooltip();
                                  if (onLaunchTool) onLaunchTool(tool);
                                }}
                              >
                                <span className="garage-matrix-chip-name">{abbreviateToolName(tool.title)}</span>
                              </div>
                              );
                            })}
                            {count > MAX_VISIBLE_CHIPS && (
                              <button
                                type="button"
                                className={`garage-matrix-cell-more${isPopoverOpen ? ' is-open' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isPopoverOpen) {
                                    closeCellPopover();
                                  } else {
                                    openCellPopover(e, row, col, cellTools);
                                  }
                                }}
                                aria-expanded={isPopoverOpen}
                                aria-haspopup="dialog"
                              >
                                +{count - MAX_VISIBLE_CHIPS} more
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tooltipNode}
      {cellPopoverNode}
    </div>
  );
};

export default GarageMatrixView;
